import _ from "lodash";
import {Fragment, useContext, useEffect} from "react";
import 'react-phone-number-input/style.css';
import {useLocation} from 'react-router-dom';
import Select from 'react-select';
import {DispatchOrder} from "../../types/DispatchOrder";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {DateUtility} from '../common/DateUtility';
import {DispatchOrdersUtility} from "../common/DispatchOrdersUtility";
import {FormUtility} from "../common/FormUtility";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PillButton} from "../common/PillButton";
import {SelectUtility} from '../common/SelectUtility';
import {UrlUtility} from "../common/UrlUtility";
import {RequestedActivityForm} from "./RequestedActivityForm";
import {ActivityHeaderDisplay} from "./ActivityHeaderDisplay";
import {DispatchOrderNotificationForm} from "./DispatchOrderNotificationForm";
import {ActivityDetail} from "./ActivityDetail";
import {DispatchOrderStatusRecords} from "./DispatchOrderStatusRecords";
import {CancellationReasonBox} from "./CancellationReasonBox";

interface DispatchOrderFormProps {
    dispatchOrder: DispatchOrder;
    showingNotificationModal: boolean;
    openNotificationModal: (statusId?: number, statusName?: string) => void;
    updateDispatchOrderStatus: (statusId: number, disableNavigationBlocker: Function, notification?: any, cancellationReasonId?: number, cancellationText?: string) => void;
    sendDispatchOrderNotification: (disableNavigationBlocker: Function, notification: any) => void;
    cancel: () => void;
    save: (formModelData: object, disableNavigationBlocker: Function) => void;
    closeNotificationModal: () => void;
    isNew: boolean;
}

export let DispatchOrderForm = function (props: DispatchOrderFormProps) {
    const dispatchOrder = _.clone(props.dispatchOrder);
    const location = useLocation()
    let queryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
    const formEngine = useModelFormEngine(
        getInitialState(),
        dispatchOrder,
        fetchSupportingData,
        validateForm,
        formStateToSaveData,
        props.save,
        Constants.dispatchOrderMessageAdditionalConfig
    );

    useEffect(() => {
        formEngine.dataStore.updateAndRender({
            saved: true,
            notificationModal: getNotificationModalState(
                formEngine.dataStore.data().supportingData?.dispatchOrderCancellationReasonOptions
            )
        });
    }, [props.showingNotificationModal]);

    useEffect(() => {
        formEngine.dataStore.updateAndRender({
            dispatch_order_status_id: props.dispatchOrder.dispatch_order_status_id
        });
    }, [props.dispatchOrder.dispatch_order_status_id]);

    function isEditableStatus(statusName: string) {
        return statusName === Constants.dispatchOrderStatusNames.requested ||
            statusName === Constants.dispatchOrderStatusNames.pending
    }

    function getInitialState() {
        return {
            id: dispatchOrder?.id,
            dispatch_order_status_id: dispatchOrder?.dispatch_order_status_id,
            activities: dispatchOrder?.activities.map((activity: any) => {
                if (
                    isEditableStatus(dispatchOrder?.dispatch_order_status?.name) &&
                    (!activity.activity_broker_assignments || activity.activity_broker_assignments.length === 0)
                ) {
                    activity.activity_broker_assignments = [getEmptyActivityBrokerAssignment(activity)]
                } else {
                    activity.activity_broker_assignments?.forEach(
                        (activityBrokerAssignment: any, activityBrokerAssignmentIndex: number) => {
                            activity.activity_broker_assignments[activityBrokerAssignmentIndex].startTimeJsDate =
                                DateUtility.dbDateToJsDate(activityBrokerAssignment.start_time)
                        }
                    )
                }
                return activity
            }),
            loading: false,
            notificationModal: getNotificationModalState()
        } as any
    }

    function getNotificationModalState(dispatchOrderCancellationReasonOptions?: any) {
        if (props.showingNotificationModal) {
            let foremanData = []
            for (let i = 0; i < (props.dispatchOrder?.notification_recipient_foremen || []).length; i++) {
                let foreman = props.dispatchOrder?.notification_recipient_foremen[i];
                let person = foreman?.person;
                if (foreman && person) {
                    foremanData.push({
                        user_id: foreman.id,
                        name: person.name,
                        phone: person.phone,
                        checked: true
                    })
                }
            }
            let notificationModalState: any = {
                showing: true,
                foremanData: foremanData,
                message: '',
            }

            if (
                queryParams.statusName === Constants.dispatchOrderStatusNames.cancelled &&
                dispatchOrderCancellationReasonOptions
            ) {
                notificationModalState.dispatch_order_cancellation_reason_id = dispatchOrderCancellationReasonOptions[0].value
            }

            return notificationModalState;
        } else {
            return {
                showing: false
            }
        }
    }

    function getEmptyActivityBrokerAssignment(activity: any) {
        return {
            start_time: activity.activity_start_time,
            startTimeJsDate: DateUtility.dbDateToJsDate(activity.activity_start_time),
            assigned_activity_material_id: activity.requested_activity_material_id,
            assigned_material_amount: activity.requested_material_amount,
            is_round_robin: false,
        }
    }

    function fetchSupportingData() {
        let returnData: any = {supportingData: {}}
        return ApiUtility.getDispatchOrderStatuses()
            .then(function (dispatchOrderStatuses) {
                returnData.supportingData.dispatchOrderStatusOptions = SelectUtility.modelArrayToSelectOptions(dispatchOrderStatuses || [])
                return ApiUtility.getBrokersForDropdown()
            })
            .then(function (brokers) {
                let assignedBrokerIds = new Set();
                (dispatchOrder?.activities || []).forEach((activity: any) => {
                    (activity.activity_broker_assignments || []).forEach((activityBrokerAssignment: any) => {
                        assignedBrokerIds.add('' + activityBrokerAssignment.broker_id);
                    });
                });
                let assignableBrokers = (brokers || []).filter((broker: any) =>
                    broker.active !== false || assignedBrokerIds.has('' + broker.id)
                );
                returnData.supportingData.brokerOptions = SelectUtility.modelArrayToSelectOptions(assignableBrokers)
                return ApiUtility.getActivityMaterials(true)
            })
            .then(function (activityMaterials) {
                returnData.supportingData.activityMaterialOptions = SelectUtility.modelArrayToSelectOptions(activityMaterials || [])
                returnData.supportingData.activityMaterials = activityMaterials || [];
                return ApiUtility.getDispatchOrderCancellationReasons()
            })
            .then(function (dispatchOrderCancellationReasons) {
                returnData.supportingData.dispatchOrderCancellationReasonOptions =
                    SelectUtility.modelArrayToSelectOptions(dispatchOrderCancellationReasons || [])
                returnData.supportingData.dispatchOrderCancellationReasons = dispatchOrderCancellationReasons || [];

                if (queryParams.statusName === Constants.dispatchOrderStatusNames.cancelled) {
                    let notificationModalUpdate = _.cloneDeep(formEngine.dataStore.data().notificationModal)
                    notificationModalUpdate.dispatch_order_cancellation_reason_id =
                        returnData.supportingData.dispatchOrderCancellationReasonOptions[0].value
                    returnData.notificationModal = notificationModalUpdate
                }
                return Promise.resolve(returnData);
            })
    }

    function getValidationData() {
        let validationErrors: string[] = [];
        let invalidFields: string[] = [];

        if ((formEngine.dataStore.data().activities || []).length === 0) {
            validationErrors.push('Invalid dispatch order - no activities.');
            invalidFields.push('activities');
        }

        (formEngine.dataStore.data().activities || []).forEach((activity: any, activityIndex: number) => {
            const activityFieldIndices = [`activity-${activityIndex}`];
            if (!activity.requested_activity_material_id) {
                validationErrors.push(`Please select a material for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('requested_activity_material_id', activityFieldIndices));
            }
            activity.activity_broker_assignments.forEach((activityBrokerAssignment: any, activityBrokerAssignmentIndex: number) => {
                const fieldIndices = [
                    `activity-${activityIndex}`,
                    `activity-broker-assignment-${activityBrokerAssignmentIndex}`
                ];
                if (!activityBrokerAssignment.broker_id) {
                    validationErrors.push(`Please select a broker for activity ${activityIndex + 1}.`);
                    invalidFields.push(FormUtility.getIndexedFieldName('broker_id', fieldIndices));
                }
                if (!activityBrokerAssignment.pit_haul && !activityBrokerAssignment.truck_count) {
                    validationErrors.push(`Please enter a truck count for activity ${activityIndex + 1}.`);
                    invalidFields.push(FormUtility.getIndexedFieldName('truck_count', fieldIndices));
                }
                if (!activityBrokerAssignment.startTimeJsDate) {
                    validationErrors.push(`Please enter a start time for activity ${activityIndex + 1}.`);
                    invalidFields.push(FormUtility.getIndexedFieldName('startTimeJsDate', fieldIndices));
                }
                if (!activityBrokerAssignment.assigned_activity_material_id) {
                    validationErrors.push(`Please select a material for activity ${activityIndex + 1}.`);
                    invalidFields.push(FormUtility.getIndexedFieldName('assigned_activity_material_id', fieldIndices));
                }
                if (!activityBrokerAssignment.assigned_material_amount) {
                    validationErrors.push(`Please enter a material amount for activity ${activityIndex + 1}.`);
                    invalidFields.push(FormUtility.getIndexedFieldName('assigned_material_amount', fieldIndices));
                }
            })
        })

        return {
            invalidFields: invalidFields,
            validationErrors: validationErrors
        }
    }

    function validateForm(handleValidationResult: Function) {
        // saving a Requested order is never blocked on completeness - the save auto-advances it to In Process; completeness is enforced only on the Confirmed transition, via getValidationData in isOptionDisabled
        return new Promise(function (resolve) {
            return handleValidationResult([], [], resolve);
        });
    }

    function formStateToSaveData(formStateToTransform: any) {
        let saveData = _.cloneDeep(formStateToTransform);

        for (let i = 0; i < saveData.activities.length; i++) {
            let activity = saveData.activities[i];
            for (let k = 0; k < activity.activity_broker_assignments.length; k++) {
                let activityBrokerAssignment = activity.activity_broker_assignments[k];
                saveData.activities[i].activity_broker_assignments[k].start_time = activityBrokerAssignment.startTimeJsDate?.toISOString()
                // a flag can never remain set while its toggle is hidden, so clear it whenever the earlier-or-equal opposite-direction condition no longer holds
                if (!DispatchOrdersUtility.brokerHasEarlierOrEqualOppositeDirectionAssignment(activityBrokerAssignment, activity, saveData.activities)) {
                    saveData.activities[i].activity_broker_assignments[k].is_round_robin = false;
                }
            }
        }

        saveData.supportingData = null;

        return saveData;
    }

    function handleDispatchOrderStatusChange(option: any) {
        if ('' + option.value !== '' + formEngine.dataStore.data().dispatch_order_status_id) {
            if (
                option.label === Constants.dispatchOrderStatusNames.confirmed ||
                option.label === Constants.dispatchOrderStatusNames.cancelled
            ) {
                props.openNotificationModal(option.value, option.label)
            } else {
                props.updateDispatchOrderStatus(option.value, formEngine.disableNavigationBlocker)
            }
        }
    }

    function onChangeActivityValue(activityIndex: number, fieldName: string, value: any) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities || []);
        updatedActivities[activityIndex][fieldName] = value;
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function onChangeActivityBrokerAssignmentValue(activityIndex: number, activityBrokerAssignmentIndex: number, fieldName: string, value: any) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities || []);
        updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex][fieldName] = value;
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function onChangeActivityBrokerAssignmentPitHaul(activityIndex: number, activityBrokerAssignmentIndex: number, pitHaul: boolean) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities || []);
        let activityBrokerAssignment = updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex];
        activityBrokerAssignment.pit_haul = pitHaul;
        if (pitHaul) {
            activityBrokerAssignment.truck_count = 0;
        }
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function forceActivityBrokerAssignmentValueToInteger(activityIndex: number, activityBrokerAssignmentIndex: number, fieldName: string) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities || []);
        let fieldValue: string | number = updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex][fieldName]
        let intValue = parseInt(fieldValue as string) || 0;
        if (fieldValue !== intValue) {
            updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex][fieldName] = intValue;
            formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
        }
    }

    function forceActivityBrokerAssignmentValueToFloat(activityIndex: number, activityBrokerAssignmentIndex: number, fieldName: string) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities || []);
        let fieldValue: string | number = updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex][fieldName]
        let floatValue = parseFloat(fieldValue as string) || 0;
        if (fieldValue !== floatValue) {
            updatedActivities[activityIndex].activity_broker_assignments[activityBrokerAssignmentIndex][fieldName] = floatValue;
            formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
        }
    }

    function getKnownAssignedTruckCountForBrokerId(brokerId: number) {
        let knownAssignedTruckCount = 0;
        let trucksAvailableToReturnByDirectionName: { [directionName: string]: number } = {
            [Constants.activityDirectionNames.inbound]: 0,
            [Constants.activityDirectionNames.outbound]: 0,
        };
        let activities = formEngine.dataStore.data().activities || [];
        let activityBrokerAssignmentsWithActivity = activities.flatMap((activity: any) =>
            (activity.activity_broker_assignments || []).map((activityBrokerAssignment: any) => ({
                activity: activity,
                activityBrokerAssignment: activityBrokerAssignment,
                // a flag whose toggle would be hidden (no earlier-or-equal opposite-direction leg) is treated as non-round-robin
                isRoundRobin: !!activityBrokerAssignment.is_round_robin &&
                    DispatchOrdersUtility.brokerHasEarlierOrEqualOppositeDirectionAssignment(activityBrokerAssignment, activity, activities),
            }))
        );
        let activityBrokerAssignmentsWithActivitySorted = _.sortBy(
            activityBrokerAssignmentsWithActivity,
            [
                (pairing: any) => pairing.activityBrokerAssignment.startTimeJsDate?.getTime() ?? 0,
                // at equal start times a non-round-robin leg is banked before a round-robin one, matching the server calc
                (pairing: any) => pairing.isRoundRobin ? 1 : 0,
            ]
        );
        for (let i = 0; i < activityBrokerAssignmentsWithActivitySorted.length; i++) {
            let activity = activityBrokerAssignmentsWithActivitySorted[i].activity;
            let activityBrokerAssignment = activityBrokerAssignmentsWithActivitySorted[i].activityBrokerAssignment;
            if (activityBrokerAssignment.broker_id !== brokerId) {
                continue;
            }
            let directionName = activity.activity_direction.name;
            let oppositeDirectionName = directionName === Constants.activityDirectionNames.inbound
                ? Constants.activityDirectionNames.outbound
                : Constants.activityDirectionNames.inbound;
            // truck_count is a text input only coerced to an integer on blur, so parse it here to avoid string concatenation in the running totals
            let truckCount = parseInt('' + activityBrokerAssignment.truck_count) || 0;
            if (activityBrokerAssignmentsWithActivitySorted[i].isRoundRobin) {
                let trucksAvailableToReturn = trucksAvailableToReturnByDirectionName[oppositeDirectionName];
                if (trucksAvailableToReturn > truckCount) {
                    trucksAvailableToReturnByDirectionName[oppositeDirectionName] = trucksAvailableToReturn - truckCount;
                } else {
                    knownAssignedTruckCount += (truckCount - trucksAvailableToReturn);
                    trucksAvailableToReturnByDirectionName[oppositeDirectionName] = 0;
                }
            } else {
                knownAssignedTruckCount += truckCount;
                trucksAvailableToReturnByDirectionName[directionName] += truckCount;
            }
        }
        return knownAssignedTruckCount;
    }

    function addActivityBrokerAssignment(activityIndex: number) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities[activityIndex].activity_broker_assignments.push(getEmptyActivityBrokerAssignment(updatedActivities[activityIndex]));
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function deleteActivityBrokerAssignment(activityIndex: number, activityBrokerAssignmentIndex: number) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities[activityIndex].activity_broker_assignments.splice(activityBrokerAssignmentIndex, 1);
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function deleteActivity(activityIndex: number) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities.splice(activityIndex, 1);
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function renderFooter() {
        let statusClass = dispatchOrder.dispatch_order_status.name.toLowerCase().replace(/\s+/g, "-");
        let statusSectionClasses = 'dispatch-order-form-footer-status-section ' + statusClass + ' '
        let notificationImageSource = '/static/images/paper-airplane-textcolor.svg';
        if (dispatchOrder.dispatch_order_status.name === Constants.dispatchOrderStatusNames.confirmed) {
            notificationImageSource = '/static/images/paper-airplane-green.svg';
        } else if (dispatchOrder.dispatch_order_status.name === Constants.dispatchOrderStatusNames.cancelled) {
            notificationImageSource = '/static/images/paper-airplane-light-gray.svg';
        }
        let disableStatusChanges = !formEngine.dataStore.data().saved
        if (disableStatusChanges) {
            statusSectionClasses += 'disabled';
        }
        let validationErrors = getValidationData().validationErrors
        return (
            <div className="dispatch-order-form-footer">
                <div className="dispatch-order-form-footer-content">
                    <div className="dispatch-order-form-footer-content-left">
                        <div className={`edits-made-label ${!formEngine.dataStore.data().saved ? 'visible' : ''}`}>
                            Edits Made
                        </div>
                    </div>
                    <div className="dispatch-order-form-footer-content-right">
                        <div className="dispatch-order-form-buttons-section">
                            <ConditionalRenderer
                                if={isEditableStatus(dispatchOrder.dispatch_order_status.name)}
                                renderer={() => (
                                    <Fragment>
                                        <PillButton
                                            containerClasses="dispatch-order-form-button-container"
                                            buttonClasses="solid-black-button submit-button dispatch-order-form-button"
                                            onClick={formEngine.onClickSave}
                                            buttonText="SAVE"
                                        />
                                    </Fragment>
                                )}
                            />
                            <PillButton
                                containerClasses="dispatch-order-form-button-container"
                                buttonClasses="light-gray-border-button cancel-button dispatch-order-form-button"
                                onClick={() => props.cancel()}
                                buttonText="CLOSE"
                            />
                        </div>
                        <div className={statusSectionClasses}>
                            <div className="dispatch-order-footer-status-section-content">
                                <div className="dispatch-order-footer-status-label">STATUS</div>
                                <div className="dispatch-order-footer-status-dropdown-container">
                                    <Select
                                        className="common-form-select dispatch-order-status-dropdown"
                                        classNamePrefix="common-form-select"
                                        value={SelectUtility.selectOptionForId(
                                            '' + formEngine.dataStore.data().dispatch_order_status_id,
                                            formEngine.dataStore.data().supportingData.dispatchOrderStatusOptions
                                        )}
                                        onChange={(option) => handleDispatchOrderStatusChange(
                                            option as SelectOption
                                        )}
                                        options={formEngine.dataStore.data().supportingData.dispatchOrderStatusOptions}
                                        menuPlacement="top"
                                        isDisabled={disableStatusChanges}
                                        isOptionDisabled={(option) => {
                                            return (
                                                option.label === Constants.dispatchOrderStatusNames.confirmed &&
                                                (
                                                    validationErrors.length > 0 ||
                                                    dispatchOrder.dispatch_order_status.name === Constants.dispatchOrderStatusNames.cancelled
                                                )
                                            )
                                        }}
                                    />
                                </div>
                                <div
                                    className="dispatch-order-message-button"
                                    onClick={() => props.openNotificationModal(undefined, undefined)}
                                >
                                    <img
                                        className="dispatch-order-message-button-icon"
                                        src={notificationImageSource}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderSummary() {
        let summaryActivitiesDisplay = [];
        for (let i = 0; i < formEngine.dataStore.data().activities.length; i++) {
            let activity = formEngine.dataStore.data().activities[i];
            const activityInfoText = '' +
                DateUtility.dbDateToDisplayTime(activity.activity_start_time) + ' | ' +
                (activity.requested_activity_material?.name ?? '') + ' | ' +
                activity.requested_material_amount + ' ' + (activity.requested_activity_material?.measurement_unit ?? '') + 's';

            summaryActivitiesDisplay.push(
                <div
                    key={i}
                    className="dispatch-order-form-summary-activity"
                >
                    <ActivityHeaderDisplay activity={activity} displayIndex={i + 1}/>
                    <div className="activity-summary-info">
                        {activityInfoText}
                    </div>
                    <div className="activity-summary-notes">
                        {activity.notes}
                    </div>
                    <img
                        className="tear-marks-image"
                        src="/static/images/tear-marks-light-gray.svg"
                    />
                </div>
            )
        }

        let requestedText = 'Requested by ' + dispatchOrder?.requested_by_user.person.name + ' ' + DateUtility.dbDateToRequestedDisplayDate(dispatchOrder.created_at);

        return (
            <div className="dispatch-order-form-body-content-left">
                <div className="dispatch-order-form-summary">
                    <div className="dispatch-order-form-summary-punch"></div>
                    <div className="dispatch-order-form-summary-activities">
                        {summaryActivitiesDisplay}
                    </div>
                    <div className="dispatch-order-form-summary-requested-by-info">
                        <img
                            className="dispatch-order-form-summary-divider"
                            src="/static/images/dashed-line-textcolor.svg"
                        />
                        <div className="dispatch-order-form-summary-requested-by">
                            {requestedText}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderForm() {
        let activityFormsDisplay = [];
        let statusHistorySection: any = null;

        for (let i = 0; i < formEngine.dataStore.data().activities.length; i++) {
            let activity = formEngine.dataStore.data().activities[i];

            let activityDisplay: any = null;
            if (isEditableStatus(dispatchOrder.dispatch_order_status.name)) {
                activityDisplay = (
                    <div
                        key={i}
                        className="dispatch-order-form-activity"
                    >
                        <RequestedActivityForm
                            addActivityBrokerAssignment={addActivityBrokerAssignment}
                            index={i}
                            formEngine={formEngine}
                            activity={activity}
                            onChangeActivityValue={onChangeActivityValue}
                            onChangeActivityBrokerAssignmentValue={onChangeActivityBrokerAssignmentValue}
                            onChangeActivityBrokerAssignmentPitHaul={onChangeActivityBrokerAssignmentPitHaul}
                            forceActivityBrokerAssignmentValueToInteger={forceActivityBrokerAssignmentValueToInteger}
                            forceActivityBrokerAssignmentValueToFloat={forceActivityBrokerAssignmentValueToFloat}
                            getKnownAssignedTruckCountForBrokerId={getKnownAssignedTruckCountForBrokerId}
                            deleteActivityBrokerAssignment={deleteActivityBrokerAssignment}
                            deleteActivity={deleteActivity}
                        />
                    </div>
                )
            } else {
                activityDisplay = (
                    <div
                        key={i}
                        className="dispatch-order-form-activity"
                    >
                        <ActivityDetail
                            index={i}
                            activity={activity}
                        />
                    </div>
                )
            }

            activityFormsDisplay.push(activityDisplay)
        }

        if (dispatchOrder.dispatch_order_status.name !== Constants.dispatchOrderStatusNames.requested) {
            statusHistorySection = (
                <DispatchOrderStatusRecords dispatchOrder={dispatchOrder}/>
            )
        }

        let formWrapperClass = "dispatch-order-form-main ";
        if (isEditableStatus(dispatchOrder.dispatch_order_status.name)) {
            formWrapperClass += "dispatch-order-edit-form";
        } else {
            formWrapperClass += "dispatch-order-display-form";
        }

        return (
            <div className={formWrapperClass}>
                <CancellationReasonBox dispatchOrder={dispatchOrder}/>
                <div className="dispatch-order-form-activities">
                    {activityFormsDisplay}
                </div>
                {statusHistorySection}
            </div>
        )
    }

    if (formEngine.dataStore.data().supportingData) {
        let header: any = null;
        let footer: any = null;
        let summary: any = null;
        let form: any = null;
        if (dispatchOrder) {
            header = <DispatchOrderFormHeader dispatchOrder={dispatchOrder}/>;
            footer = renderFooter();
            summary = renderSummary();
            form = renderForm();
        }

        return (
            <div className="common-form-container dispatch-order-form-container">
                <ConditionalRenderer
                    if={formEngine.dataStore.data().loading}
                    renderer={() => (<OverlayLoadingIndicator/>)}
                />
                {header}
                <div className="common-form dispatch-order-form">
                    <div className="common-form-body">
                        <div className="dispatch-order-form-body-content">
                            {summary}
                            <div className="dispatch-order-form-body-content-right">
                                {form}
                            </div>
                        </div>
                    </div>
                </div>
                {footer}
                <ConditionalRenderer if={formEngine.dataStore.data().notificationModal.showing} renderer={() => (
                    <DispatchOrderNotificationForm
                        close={props.closeNotificationModal}
                        statusId={queryParams.statusId}
                        statusName={queryParams.statusName}
                        updateDispatchOrderStatus={props.updateDispatchOrderStatus}
                        sendDispatchOrderNotification={props.sendDispatchOrderNotification}
                        formEngine={formEngine}
                    />
                )}/>
                <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker}/>
            </div>
        );
    } else {
        return null;
    }
};

let DispatchOrderFormHeader = function (props: any) {
    let context = useContext(GlobalContext);

    function navigateToJob() {
        context.navigator.navigateTo('/' + Constants.routes.jobs.pathSegment + '/' + props.dispatchOrder.job.id);
    }

    return (
        <div className="dispatch-order-form-header">
            <div className="dispatch-order-form-header-content">
                <div className="dispatch-order-form-header-content-left">
                    <div className="dispatch-order-header-job-number-box">
                        <div className="dispatch-order-header-job-number">
                            {props.dispatchOrder.job.job_number}
                        </div>
                    </div>
                    <div className="dispatch-order-header-job-link" onClick={navigateToJob}>
                        <div className="dispatch-order-header-job-name">
                            {props.dispatchOrder.job.name}
                        </div>
                        <img
                            className="dispatch-order-header-job-info-icon"
                            src="/static/images/info-green.svg"
                        />
                    </div>
                </div>
                <div className="dispatch-order-form-header-content-right">
                    <div className="dispatch-order-header-date-section">
                        <img
                            className="dispatch-order-header-date-icon"
                            src="/static/images/calendar-green.svg"
                        />
                        <div className="dispatch-order-header-date-label">ACTIVITY DATE</div>
                        <div className="dispatch-order-header-date">
                            {DateUtility.dbDateToDispatchOrderHeaderDate(props.dispatchOrder.requested_for_date)}
                        </div>
                    </div>
                    <div className="dispatch-order-header-requested-by-section">
                        <div className="dispatch-order-header-requested-by-name">
                            {props.dispatchOrder.requested_by_user.person.name}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
